import { ui } from '@procuros/datachecks';

import { logError } from 'services/logging/logging';
import {
  doesSectionContainPath,
  findMatchingSubSection,
  getFirstFieldFromSection,
  hasFields,
  hasSubSections,
  normalizeAndCleanPath,
} from 'services/schemableFormNavigation/uiConfigPathHelpers';
import { GenericError } from 'support/helpers/errors/errors';

/**
 * Given a candidate annotation item id (= form-field path candidate) and the
 * uiConfig, returns either:
 * - the candidate unchanged (leaf exists OR section-level path)
 * - the id of the first leaf cell in the matching section/sub-section
 * - undefined when the section type isn't in uiConfig at all OR when a
 *   matching section/sub-section is empty (annotation is unreachable —
 *   caller logs the generic drop envelope).
 *
 * Mirrors remapErrorsToFirstCell's behavior so that annotation items end up
 * at the same DOM cells as form errors would for the same canonical path.
 *
 * Diagnostic policy: we log a specific reason BEFORE returning undefined so
 * that prod telemetry can distinguish missing-section vs empty-fields vs
 * empty-sub-section misconfigurations. The caller (`mapAnnotation.ts`) still
 * logs its own generic "dropping" envelope. This matches the form-error
 * pipeline's behavior — see `remapErrorsToFirstCell.ts` for the parity target.
 */
export const remapAnnotationToFirstCell = (candidateId: string, uiConfig: Array<ui.Section>): string | undefined => {
  const [section, rowIndex, ...fieldPath] = candidateId.split('.');
  const joinedFieldPath = fieldPath.join('.');

  const matchingSection = uiConfig.find((s) => s.type === section);
  if (!matchingSection) {
    logError(
      new GenericError('WebEDI: annotation path references a section type not in uiConfig', {
        candidateId,
        section,
      }),
    );
    return undefined;
  }

  if (!rowIndex) {
    return candidateId;
  }

  if (doesSectionContainPath(matchingSection, joinedFieldPath)) {
    return candidateId;
  }

  if (hasFields(matchingSection)) {
    const firstField = matchingSection.fields[0];
    if (!firstField) {
      logError(
        new GenericError('WebEDI: annotation section has no fields — dropping', {
          candidateId,
          section,
        }),
      );
      return undefined;
    }
    return `${section}.${rowIndex}.${firstField.path}`;
  }

  if (hasSubSections(matchingSection)) {
    const matchingSubSection = findMatchingSubSection(matchingSection, joinedFieldPath);
    if (!matchingSubSection) {
      logError(
        new GenericError('WebEDI: annotation has no matching sub-section — dropping', {
          candidateId,
          section,
          joinedFieldPath,
        }),
      );
      return undefined;
    }
    const firstField = getFirstFieldFromSection(matchingSubSection);
    if (!firstField) {
      logError(
        new GenericError('WebEDI: annotation sub-section has no fields — dropping', {
          candidateId,
          section,
          subSectionPath: matchingSubSection.path,
        }),
      );
      return undefined;
    }
    const subSectionPath = normalizeAndCleanPath(matchingSubSection.path);
    const subSectionRowIndex = fieldPath[1] || '0';
    return `${section}.${rowIndex}.${subSectionPath}.${subSectionRowIndex}.${firstField.path}`;
  }

  return undefined;
};
